import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiChrome, FiGlobe, FiInfo, FiArrowRight, FiGithub } = FiIcons;

const PomodoroTooltip = ({ children }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <span 
      className="relative inline-block cursor-help group"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-haspopup="true"
    >
      <span className="underline decoration-orange-500/50 decoration-2 underline-offset-4 group-hover:decoration-orange-500 transition-colors">
        {children}
      </span>
      <AnimatePresence>
        {isHovered && (
          <motion.span
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-64 p-4 bg-slate-800 border border-orange-500/30 rounded-xl shadow-2xl z-50 text-sm font-normal text-slate-200 text-left pointer-events-none"
            role="tooltip"
          >
            <span className="flex items-center text-orange-400 font-bold mb-1">
              <SafeIcon icon={FiInfo} className="mr-2" />
              Pomodoro-Technik
            </span>
            Fokussiertes Arbeiten in 25-Minuten-Phasen, gefolgt von erholsamen Pausen.
            <span className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-slate-800 border-r border-b border-orange-500/30 rotate-45"></span>
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
};

const Hero = () => {
  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -z-10" aria-hidden="true"></div>
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -z-10" aria-hidden="true"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <h1 className="text-5xl md:text-7xl font-extrabold text-white tracking-tight mb-2">
            Ergo<span className="text-orange-500">Focus</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-400 font-medium tracking-wide mb-6">
            Pomodoro Timer
          </p>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
            Produktivität trifft <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-green-400 to-blue-400">
              Wohlbefinden am Bildschirm.
            </span>
          </h2>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-slate-400">
            Der intelligente <PomodoroTooltip>Pomodoro-Timer</PomodoroTooltip> mit ergonomischen Pausen-Übungen für Chrome & Firefox. 
            Fokussiere dich auf deine Arbeit und bleibe gesund mit geführten Übungen.
          </p>
        </motion.div>

        <motion.div 
          className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <a 
            href="https://chrome.google.com/webstore" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full sm:w-auto flex items-center justify-center px-8 py-4 border border-transparent text-lg font-bold rounded-xl text-slate-950 bg-white hover:bg-orange-500 hover:text-white transition-all shadow-xl hover:shadow-orange-500/40 group active:scale-95"
            aria-label="ErgoFocus für Chrome installieren"
          >
            <SafeIcon icon={FiChrome} className="mr-3 text-2xl" />
            <span>Für Chrome hinzufügen</span>
            <SafeIcon icon={FiArrowRight} className="ml-3 opacity-0 group-hover:opacity-100 transition-all transform group-hover:translate-x-2" />
          </a>
          
          <button 
            disabled 
            type="button"
            className="w-full sm:w-auto flex items-center justify-center px-8 py-4 border border-slate-700 text-lg font-medium rounded-xl text-slate-400 bg-slate-800/50 cursor-not-allowed transition-all"
            aria-label="Firefox Version bald verfügbar"
          >
            <SafeIcon icon={FiGlobe} className="mr-3 text-2xl" />
            Firefox (In Kürze)
          </button>
        </motion.div>

        {/* GitHub Button - Smaller as requested */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center"
        >
          <a 
            href="https://github.com/ecomcodeLab/ErgoFocus-Pomodoro-Extension-Chrome-Work"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-800 border border-slate-700 hover:border-slate-600 rounded-full text-sm font-medium text-slate-300 hover:text-white transition-all active:scale-95"
          >
            <SafeIcon icon={FiGithub} />
            <span>Open Source auf GitHub</span>
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="mt-16 mx-auto max-w-sm relative"
        >
          <div className="absolute inset-0 bg-orange-500/20 blur-3xl rounded-full" aria-hidden="true"></div>
          <div className="relative rounded-2xl bg-[#161a2b] p-2 shadow-2xl border border-slate-700/50 hover:border-orange-500/30 transition-colors">
            <img 
              src="/ergofocus-extension-popup.png" 
              alt="Vorschau der ErgoFocus Browser-Extension" 
              className="w-full h-auto rounded-xl" 
              loading="lazy"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;